import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import ScoreCard from '../../components/ScoreCard';
import ScoreTrendChart from '../../components/ScoreTrendChart';
import RuleTriggerList from '../../components/RuleTriggerList';
import LoadingSpinner from '../../components/LoadingSpinner';
import TrustStack from '../../components/TrustStack';
import MaturityDisclaimer from '../../components/MaturityDisclaimer';
import DownloadProfilePdfButton from '../../components/DownloadProfilePdfButton';
import ScoreDisclaimer from '../../components/ScoreDisclaimer';
import { disputesApi } from '../../services/disputes';
import { vendorApi } from '../../services/vendor';
import { scoringApi } from '../../services/scoring';
import { toGGScore, toGGDelta, getScoreColor, getScoreColorClass } from '../../utils/score';

export default function VendorDashboard() {
  const navigate = useNavigate();

  const { data: org, isLoading: orgLoading } = useQuery({
    queryKey: ['vendor', 'organization'],
    queryFn: () => vendorApi.getOrganization().then((r) => r.data),
  });

  const { data: sites } = useQuery({
    queryKey: ['vendor', 'sites', 'summary'],
    queryFn: () => vendorApi.getSites({ page: '1' }).then((r) => r.data),
  });

  const { data: guards } = useQuery({
    queryKey: ['vendor', 'guards', 'summary'],
    queryFn: () => vendorApi.getGuards({ page: '1' }).then((r) => r.data),
  });

  const { data: scoreHistory } = useQuery({
    queryKey: ['vendor', 'score-history', org?.id],
    queryFn: () => scoringApi.getVendorScoreHistory('vendor', org!.id),
    enabled: !!org?.id,
  });

  const { data: ruleTriggers } = useQuery({
    queryKey: ['vendor', 'rule-triggers', org?.id],
    queryFn: () => scoringApi.getVendorRuleTriggers('vendor', org!.id),
    enabled: !!org?.id,
  });

  const { data: topGuards } = useQuery({
    queryKey: ['vendor', 'top-guards'],
    queryFn: () => vendorApi.getTopGuards(5).then((r) => r.data),
  });

  const { data: recentIssues } = useQuery({
    queryKey: ['vendor', 'recent-issues'],
    queryFn: () => vendorApi.getRecentIssues(10).then((r) => r.data),
  });

  // Feature 7: count-only indicator of critical-override triggers awaiting
  // admin review.  Vendors see "N pending review(s)" with no detail.
  const { data: pendingReview } = useQuery({
    queryKey: ['vendor', 'pending-review-count'],
    queryFn: () => vendorApi.getPendingReviewCount().then((r) => r.data),
  });

  // Feature 9: vendor's open dispute count for dashboard badge.
  const { data: openDisputes } = useQuery({
    queryKey: ['vendor', 'disputes', 'open-count'],
    queryFn: () => disputesApi.openCount(),
  });

  const [issueTab, setIssueTab] = useState<'all' | 'critical' | 'warnings'>('all');

  if (orgLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const vendorScore = org?.current_vendor_score ?? 70;
  const totalSites = sites?.count ?? 0;
  const totalGuards = guards?.count ?? 0;
  const snapshots = scoreHistory?.results ?? [];
  const previousScore = snapshots.length >= 2
    ? Number(snapshots[snapshots.length - 2].score_value)
    : null;

  // Compute avg scores from first page of data
  // Note: DRF DecimalField returns scores as strings, so cast with Number()
  const validSiteScores = (sites?.results ?? []).filter(s => s.score != null && !isNaN(Number(s.score)));
  const avgSiteScore = validSiteScores.length
    ? Math.round(validSiteScores.reduce((sum, s) => sum + Number(s.score), 0) / validSiteScores.length)
    : null;
  const validGuardScores = (guards?.results ?? []).filter(g => g.score != null && !isNaN(Number(g.score)));
  const avgGuardScore = validGuardScores.length
    ? Math.round(validGuardScores.reduce((sum, g) => sum + Number(g.score), 0) / validGuardScores.length)
    : null;

  // Filter issues by tab
  const filteredIssues = (recentIssues ?? []).filter((issue) => {
    if (issueTab === 'critical') return issue.severity_level === 'critical' || issue.severity_level === 'high';
    if (issueTab === 'warnings') return issue.severity_level === 'medium' || issue.severity_level === 'low';
    return true;
  });

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="Overview of your security performance"
        actions={<DownloadProfilePdfButton />}
      />

      {/* Feature 3: maturity banner when the account hasn't passed the publish gate */}
      <MaturityDisclaimer
        when={!!org?.publish_status && !org.publish_status.is_publishable}
      />

      {/* Feature 9: open-disputes badge → click to view list */}
      {openDisputes && openDisputes.count > 0 && (
        <Link
          to="/vendor/disputes"
          className="mb-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 hover:bg-slate-100"
        >
          <span className="text-slate-600">&#9881;</span>
          <span className="text-sm text-slate-700">
            {openDisputes.count} open dispute{openDisputes.count === 1 ? '' : 's'} under review
          </span>
        </Link>
      )}

      {pendingReview && pendingReview.count > 0 && (
        <div
          className="mb-4 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
          title="An admin is reviewing flagged reports. No detail is shared while the review is pending."
        >
          <span className="text-amber-600">&#9888;</span>
          <span className="text-sm text-amber-900">
            {pendingReview.count} pending review
            {pendingReview.count === 1 ? '' : 's'}
          </span>
        </div>
      )}

      {/* Score Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <Link to="/vendor/analytics" className="block hover:opacity-90 transition-opacity">
          <ScoreCard label="Vendor Score" score={vendorScore} previousScore={previousScore} size="lg" />
        </Link>
        {avgSiteScore != null && (
          <Link to="/vendor/sites" className="block hover:opacity-90 transition-opacity">
            <ScoreCard label="Avg Site Score" score={avgSiteScore} />
          </Link>
        )}
        {avgGuardScore != null && (
          <Link to="/vendor/guards" className="block hover:opacity-90 transition-opacity">
            <ScoreCard label="Avg Guard Score" score={avgGuardScore} />
          </Link>
        )}
      </div>

      {/* Feature 2: Trust labels under the score cards */}
      {org?.trust_stack && (
        <div className="mb-4">
          <TrustStack data={org.trust_stack} variant="compact" />
        </div>
      )}

      {/* Feature 10: spec-locked legal disclaimer with Read more modal */}
      <div className="mb-6">
        <ScoreDisclaimer variant="short" />
      </div>

      {/* Score Trend Chart */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Score Trend</h2>
        <ScoreTrendChart snapshots={snapshots} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Recent Issues */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Recent Issues</h2>
            <div className="flex gap-1">
              {(['all', 'critical', 'warnings'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setIssueTab(tab)}
                  className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                    issueTab === tab
                      ? 'bg-brand-100 text-brand-700'
                      : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {tab === 'all' ? 'All' : tab === 'critical' ? 'Critical' : 'Warnings'}
                </button>
              ))}
            </div>
          </div>
          {filteredIssues.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Issue</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Entity</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Impact</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredIssues.map((issue) => (
                    <tr key={issue.id}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900">{issue.rule_name}</p>
                        <p className="text-xs text-slate-400">{issue.rule_category}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-700">{issue.entity_name}</p>
                        <p className="text-xs text-slate-400 capitalize">{issue.entity_type}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-semibold ${issue.score_impact >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {issue.score_impact >= 0 ? '+' : ''}{toGGDelta(issue.score_impact)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400">
                        {new Date(issue.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-4 text-center">No issues found for this filter.</p>
          )}
        </div>

        {/* Top Performing Guards */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Top Performing Guards</h2>
          {topGuards && topGuards.length > 0 ? (
            <div className="space-y-3">
              {topGuards.map((guard, idx) => (
                <button
                  key={guard.id}
                  onClick={() => navigate(`/vendor/guards/${guard.id}`)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left"
                >
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-xs font-bold text-white">
                    {idx + 1}
                  </span>
                  <span className="flex-1 text-sm font-medium text-slate-900 truncate">{guard.name}</span>
                  <span className={`text-sm font-bold ${getScoreColorClass(getScoreColor(guard.score))}`}>
                    {toGGScore(guard.score)}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No guards registered yet.</p>
          )}
        </div>
      </div>

      {/* Rule Triggers - This Week */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <RuleTriggerList
          triggers={ruleTriggers?.results ?? []}
          title="Recent Score Changes"
        />
      </div>
    </>
  );
}
